import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query'
import {
  getBookingFormReferenceOptions,
  getBookingGuestScreenOptions,
  getBookingOptions,
  listBookingsInfiniteOptions,
  nextCursorParam,
  STALE,
  type BookingList,
  type ListBookingsData,
} from '@/shared/api'

/** Параметры запроса `GET /bookings` — как их сгенерировал кодоген из спеки. */
type BookingsQueryParams = NonNullable<ListBookingsData['query']>

/**
 * Фильтр списка броней. Литералы НЕ переписаны руками, а **выведены из контракта**.
 *
 * Руками их писать нельзя: это была бы третья копия одного перечисления (контракт, zod-схема
 * URL в `app/router/search.ts`, и здесь). Добавят на бекенде новый статус — рукописная копия
 * промолчит, чип фильтра просто не появится, и никто этого не заметит: ни линт, ни тайпчек
 * не видят пропущенный вариант, потому что подмножество литералов — валидный тип.
 *
 * Выведенный тип, наоборот, обновится сам, и всё, что перечисляет статусы через
 * `Record<BookingsFilter['status'], …>`, **перестанет компилироваться**, пока не добавят
 * недостающий вариант. Ошибка станет громкой.
 *
 * Zod-схема в `app/router/search.ts` остаётся рукописной — zod требует литералов и вывести
 * их из типа нельзя. Но она валидирует URL, а не ходит на сервер: разъехавшись с контрактом,
 * она отбросит незнакомый `?status=`, а не отправит его на бекенд.
 */
export type BookingsFilter = {
  status: NonNullable<BookingsQueryParams['status']>
  sort: NonNullable<BookingsQueryParams['sort']>
}

/**
 * Список броней (`GET /bookings`) — курсорная докрутка кнопкой «Показать ещё».
 *
 * План фазы предполагал, что кодоген может не дать `...InfiniteOptions` и предписывал в этом
 * случае собирать `infiniteQueryOptions` руками поверх `listBookings` из `sdk.gen`. Это не
 * понадобилось: плагин `@tanstack/react-query` в этой версии кодогена уже генерирует
 * `listBookingsInfiniteOptions` сам (см. `generated/@tanstack/react-query.gen.ts`). Сам он не
 * задаёт `initialPageParam`/`getNextPageParam` — это заведомо оставлено вызывающей стороне,
 * их и доопределяем здесь.
 *
 * `initialPageParam: {}` — пустой объект, а НЕ пустая строка. Кодоген оборачивает pageParam
 * так: строка идёт в `query.cursor` буквально, а объект подмешивается в query как есть (см.
 * `createInfiniteParams` в `react-query.gen.ts`). `''` ушла бы на сервер как `cursor=''` —
 * это не то же самое, что «курсора нет» («Без него — первая страница», контракт). `{}` не
 * добавляет `cursor` в query вовсе.
 *
 * `filters` в ответе сервер считает по ВСЕЙ выборке компании, а не по текущей странице —
 * складывать чипы разных загруженных страниц нельзя, для панели фильтров нужно значение
 * последней полученной страницы как есть. ⚠️ Поле звалось `counts` и было закрытым объектом из
 * шести ключей; вместе с подписями и порядком оно переехало на сервер, а бронь со статусом вне
 * тех шести не попадала ни под один чип.
 */
export function bookingsQuery(filter: BookingsFilter) {
  return infiniteQueryOptions({
    ...listBookingsInfiniteOptions({ query: filter }),
    initialPageParam: {},
    getNextPageParam: (lastPage: BookingList) => nextCursorParam(lastPage.nextCursor),
    staleTime: STALE.bookings,
    // refetchInterval сюда НЕ добавлять: на infinite-запросе поллинг перезапрашивает КАЖДУЮ
    // уже загруженную страницу, а не только последнюю (см. HANDOFF.md, «Поллинг на
    // infinite-запросе перезапрашивает ВСЕ страницы»). Список броней — экран для просмотра,
    // ему хватает refetchOnWindowFocus (глобальный дефолт в app/providers/query-client.ts).
  })
}

/**
 * Карточка брони (`GET /bookings/{bookingId}`).
 *
 * `staleTime: STALE.bookingDetail` — на нём держится дешевизна префетча по наведению:
 * `queryClient.prefetchQuery` сам не выполняет запрос, если данные ещё не протухли, поэтому
 * повторное наведение на уже загруженную бронь в пределах этого окна не даёт сетевого запроса.
 */
export function bookingDetailQuery(bookingId: string) {
  return queryOptions({
    ...getBookingOptions({ path: { bookingId } }),
    staleTime: STALE.bookingDetail,
  })
}

/**
 * Живой экран гостя (3.3) — отдельный запрос, а не поле карточки: состояние волатильное и нужно
 * только тогда, когда его открывают. `staleTime: 0` — поповер про «сейчас», и показывать в нём
 * прошлое чтение значило бы отвечать не на тот вопрос.
 */
export function bookingGuestScreenQuery(bookingId: string) {
  return queryOptions({
    ...getBookingGuestScreenOptions({ path: { bookingId } }),
    staleTime: STALE.guestScreen,
  })
}

/** Обёртка над `queryClient.prefetchQuery` — дёргается из `useHoverIntent` при наведении на строку. */
export function prefetchBookingDetail(queryClient: QueryClient, bookingId: string) {
  return queryClient.prefetchQuery(bookingDetailQuery(bookingId))
}

/**
 * Справочники формы брони (`GET /reference/booking-form`): объекты, площадки-источники, каналы.
 *
 * **Всё это приезжает с сервера, а не лежит константой во фронте** — и это не педантизм. В макете
 * (prototype/Prototype.dc.html:2073-2075) все три списка захардкожены: одиннадцать квартир, шесть
 * площадок, четыре мессенджера. Квартиры у каждой компании свои и меняются в Студии; набор
 * подключённых каналов — тоже (Prima Connect), и он зависит от того, что компания подключила.
 * Список во фронте протух бы в день первой же новой квартиры, и менеджер не смог бы завести на
 * неё бронь — молча, без единой ошибки на экране.
 *
 * Живёт в `entities/booking`, а не в фиче: это данные ДОМЕНА брони — «на что можно забронировать,
 * откуда бронь пришла, чем писать гостю», — а не поля одной формы.
 *
 * ⚠️ Второго потребителя у справочника НЕТ, и обоснование места держится на природе данных, а не
 * на обещанном соседе: `PATCH` сведён с доменом и принимает ТРИ поля — число гостей и два
 * примечания, — ни одно из которых отсюда не берётся.
 *
 * `staleTime: STALE.reference` — справочник меняется раз в недели, а не в секунды; перезапрашивать
 * его на каждое открытие модалки незачем.
 */
export function bookingFormQuery() {
  return queryOptions({
    ...getBookingFormReferenceOptions(),
    staleTime: STALE.reference,
  })
}
